import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {Pagination} from 'lib/Listings';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import useLimitOffset, {AVAILABLE_LIMITS} from 'orgs/usePoliceablePolicyTableLimitOffset';
import {miniSpacingLeft, smallSpacingTop} from 'styles/Utilities';
import Tooltip from 'ui-library/bs/Tooltip';
import {GRAY_DARKER, BLUE_LIGHT, GRAY_LIGHTER, GRAY_LIGHT, ORANGE} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ResultsPager from 'ui-library/ResultsPager';
import {Cell, CellType} from 'ui-library/simple-table/SimpleTable';

import {
  BenchlingAppActivitySessionsQuery,
  BenchlingAppActivitySessionsQueryVariables,
} from './__generated/BenchlingAppActivitySessionsQuery';
import BenchlingAppSessionActivityFilters from './BenchlingAppSessionActivityFilters';
import BenchlingAppSessionActivityTableRows from './BenchlingAppSessionActivityTableRows';
import ExportSessionActivityDropdown, {APP_STATUS_EXPORT_LIMIT} from './ExportSessionActivityDropdown';
import NoSessionsCard from './NoSessionsCard';

type BenchlingApp = BenchlingAppActivitySessionsQuery['benchlingApp'];

type AccessibleApp = ExtractByTypename<BenchlingApp, 'BenchlingAppNode'>;

type BenchlingAppSessionNode = AccessibleApp['sessionListing']['sessions'][number];

export const BENCHLING_APP_SESSIONS_QUERY = gql`
  query BenchlingAppActivitySessionsQuery($appId: StrictString!, $limitOffsetInput: LimitOffsetInput!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingApp(id: $appId) {
      ... on BenchlingAppNode {
        id
        isActivityTabVisible
        sessionListing(limitOffsetInput: $limitOffsetInput) {
          total
          sessions {
            ...BenchlingAppSessionGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BenchlingAppSession').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface SessionActivityTableProps {
  appId: string;
}

export interface SessionActivityPagedTablePropsLoading {
  isLoading: true;
  setOffset: (offset: number) => void;
  setLimit: (offset: number) => void;
  pagination: Pagination;
}

export interface SessionActivityPagedTablePropsLoaded {
  isLoading: false;
  app: AccessibleApp;
  setOffset: (offset: number) => void;
  setLimit: (offset: number) => void;
  pagination: Pagination;
  filteredSessions: Array<BenchlingAppSessionNode>;
}

export interface TableOrLoadingContainerPropsLoaded extends SessionActivityPagedTablePropsLoaded {
  areRowsCollapsed: Map<string, boolean>;
  isCollapsedCallback: (sessionId: string, isCollapsed: boolean) => void;
}

export type SessionActivityPagedTableProps =
  | SessionActivityPagedTablePropsLoading
  | SessionActivityPagedTablePropsLoaded;

export type TableOrLoadingContainerProps =
  | SessionActivityPagedTablePropsLoading
  | TableOrLoadingContainerPropsLoaded;

export default function BenchlingAppSessionActivityTable(
  props: SessionActivityTableProps
): JSX.Element | null {
  const [{limit, offset}, {setLimit, setOffset}] = useLimitOffset();
  const [benchlingApp, updateBenchlingApp] = useState<BenchlingApp>();

  const [modifiedAtFilter, setModifiedAtFilter] = useState<{
    fromDatetime: string | null;
    toDatetime: string | null;
  }>({fromDatetime: null, toDatetime: null});
  const [sessionNameFilter, setSessionNameFilter] = useState<string | null>(null);
  const [sessionStatusFilter, setSessionStatusFilter] = useState<Array<string>>([]);
  const [filteredSessions, setFilteredSessions] = useState<Array<BenchlingAppSessionNode>>([]);
  const [allSessions, setAllSessions] = useState<Array<BenchlingAppSessionNode>>([]);

  const onQueryComplete = (data: BenchlingAppActivitySessionsQuery): void => {
    const app: BenchlingApp = data.benchlingApp;
    updateBenchlingApp(app);
    if (!isInaccessible(app)) {
      setAllSessions(app.sessionListing.sessions);
    }
  };

  const batchedPollingQuery = useBenchlingQuery<
    BenchlingAppActivitySessionsQuery,
    BenchlingAppActivitySessionsQueryVariables
  >(BENCHLING_APP_SESSIONS_QUERY, {
    variables: {appId: props.appId, limitOffsetInput: {limit: 10, offset: 0}},
    pollInterval: BenchlingConfig.BENCHLING_APP_SESSIONS_POLLING_FREQUENCY_MS,
    renderCachedDataWhenRefetching: true,
    onCompleted: onQueryComplete,
  });
  if (
    batchedPollingQuery.type === QueryResultType.PENDING ||
    batchedPollingQuery.type === QueryResultType.ERROR ||
    typeof benchlingApp === 'undefined'
  ) {
    return (
      <BenchlingAppSessionActivityPagedTable
        isLoading={true}
        setOffset={setOffset}
        setLimit={setLimit}
        pagination={{offset, limit, total: 0}}
      />
    );
  }
  if (isInaccessible(benchlingApp)) {
    return <></>;
  }

  return (
    <>
      {benchlingApp.isActivityTabVisible ? (
        benchlingApp.sessionListing.total === 0 ? (
          <div>
            <Header1 className={css(styles.activityHeader)}>Activity</Header1>
            <NoSessionsCard />
          </div>
        ) : (
          <div>
            <div className={classNames('u-lr-split', css(styles.activityHeader))}>
              <Header1>Activity</Header1>
              <div className={css(styles.buttonStyling)}>
                {benchlingApp.sessionListing.total <= APP_STATUS_EXPORT_LIMIT ? (
                  <></>
                ) : (
                  <Tooltip
                    title="Data exceeds table limits. Only the most recent 100k rows of data are displayed and available for export."
                    container="body"
                  >
                    <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
                  </Tooltip>
                )}
                &nbsp;&nbsp;
                <ExportSessionActivityDropdown
                  benchlingApp={benchlingApp}
                  modifiedAtFilter={modifiedAtFilter}
                  sessionNameFilter={sessionNameFilter}
                  sessionStatusFilter={sessionStatusFilter}
                  filteredSessions={filteredSessions}
                />
              </div>
            </div>
            <BenchlingAppSessionActivityFilters
              modifiedAtFilter={modifiedAtFilter}
              setModifiedAtFilter={setModifiedAtFilter}
              sessionNameFilter={sessionNameFilter}
              setSessionNameFilter={setSessionNameFilter}
              sessionStatusFilter={sessionStatusFilter}
              setSessionStatusFilter={setSessionStatusFilter}
              allSessions={allSessions}
              setFilteredSessions={setFilteredSessions}
              setOffset={setOffset}
            />
            <BenchlingAppSessionActivityPagedTable
              isLoading={false}
              app={benchlingApp}
              setOffset={setOffset}
              setLimit={setLimit}
              pagination={{offset, limit, total: filteredSessions.length}}
              filteredSessions={filteredSessions}
            />
          </div>
        )
      ) : (
        <div className={classNames('u-lr-split', 'u-spacing-top', css(styles.inaccessible))}>
          <div>
            <Header1>Activity</Header1>
            <div>
              Insufficient permissions to view app activity. Only the app's creator and tenant admins may view
              app activity.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function BenchlingAppSessionActivityPagedTable(props: SessionActivityPagedTableProps): JSX.Element {
  function allClosedMap(): Map<string, boolean> {
    const newMap = new Map<string, boolean>();
    if (!props.isLoading) {
      for (const session of props.app.sessionListing.sessions) {
        newMap.set(session.id, true);
      }
    }
    return newMap;
  }

  const [areRowsCollapsed, setAreRowsCollapsed] = useState<Map<string, boolean>>(allClosedMap());

  function collapseAll(): void {
    setAreRowsCollapsed(allClosedMap());
  }

  function setRowForSessionIsCollapsed(sessionId: string, isCollapsed: boolean): void {
    const newMap = new Map(areRowsCollapsed);
    newMap.set(sessionId, isCollapsed);
    setAreRowsCollapsed(newMap);
  }

  return (
    <>
      <div className={css(styles.resultPagerContainer)}>
        <div className={css(styles.resultPagerContainerContent)}>
          <ResultsPager
            allowFastForwardAndBackward={true}
            setOffset={props.setOffset}
            isLoading={props.isLoading}
            pagination={props.pagination}
          />
        </div>
        <div className={css(styles.resultPagerContainerContent)}>
          <ListingSettingsDropdown
            pageSizeConfig={{
              availablePageSizes: AVAILABLE_LIMITS,
              pageSize: props.pagination.limit,
              onSetPageSize: props.setLimit,
            }}
            iconAltText="Results Per Page"
          />
        </div>
        <div className={css(styles.collapseButton)} onClick={collapseAll}>
          Collapse all
        </div>
        {!props.isLoading && props.app.sessionListing.total > props.app.sessionListing.sessions.length ? (
          <div className={css(styles.limitMessage)}>
            (Most recent {props.app.sessionListing.sessions.length} of {props.app.sessionListing.total})
          </div>
        ) : null}
      </div>
      {props.isLoading ? (
        <TableOrLoadingContainer {...props} />
      ) : (
        <TableOrLoadingContainer
          areRowsCollapsed={areRowsCollapsed}
          isCollapsedCallback={setRowForSessionIsCollapsed}
          {...props}
        />
      )}
    </>
  );
}

export function TableOrLoadingContainer(props: TableOrLoadingContainerProps): JSX.Element {
  if (props.isLoading) {
    return (
      <div className={css(styles.tableOrLoadingContainer)}>
        <CenteredHorizontalThrobber />
      </div>
    );
  }
  if (props.filteredSessions.length === 0) {
    return <NoSessionsCard />;
  }

  const filteredSessions = props.filteredSessions;
  const limit = props.pagination.limit;
  const offset = props.pagination.offset;
  const pagedSessions = filteredSessions.slice(offset, offset + limit);
  const headerRow = (
    <tr className={css(styles.header)}>
      <th />
      <Cell className={css(styles.headerCell)} cellTypeOverride={CellType.HEADER}>
        Status
      </Cell>
      <Cell className={css(styles.headerCell)} cellTypeOverride={CellType.HEADER}>
        Session name
      </Cell>
      <Cell className={css(styles.headerCell)} cellTypeOverride={CellType.HEADER}>
        Modified at
      </Cell>
    </tr>
  );
  return (
    <table>
      <thead>{headerRow}</thead>
      <BenchlingAppSessionActivityTableRows
        sessions={pagedSessions}
        areRowsCollapsed={props.areRowsCollapsed}
        isCollapsedCallback={props.isCollapsedCallback}
        dataTestAttrs={{component: 'SessionActivityTableRows', element: 'root'}}
      />
    </table>
  );
}

export const styles = StyleSheet.create({
  inaccessible: {
    padding: '16px 24px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  activityHeader: {
    height: '48px',
    lineHeight: '48px',
    paddingLeft: '12px',
    marginBottom: '0px',
    borderBottom: '1px solid #E8F0F2',
  },
  header: {
    height: '32px',
    background: GRAY_LIGHTER,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  headerCell: {
    paddingLeft: '0px',
  },
  collapseButton: {
    display: 'inline-block',
    marginTop: '5px',
    marginLeft: '8px',
    cursor: 'pointer',
    color: GRAY_DARKER,
    ':hover': {
      color: BLUE_LIGHT,
    },
  },
  resultPagerContainer: {...miniSpacingLeft, ...smallSpacingTop, paddingLeft: 12},
  resultPagerContainerContent: {
    display: 'inline-block',
    verticalAlign: 'top',
  },
  tableOrLoadingContainer: {
    ...smallSpacingTop,
    width: '100%',
  },
  buttonStyling: {
    margin: '10px',
  },
  warningIcon: {
    marginRight: '3px',
    color: ORANGE,
  },
  limitMessage: {
    display: 'inline-block',
    marginTop: '5px',
    marginLeft: '8px',
    color: GRAY_DARKER,
  },
});
